import api from "api/api";
import {
  CatStepsDto,
  CatToolProjectLinkDto,
  DeliveryFileMappingOptionsDto,
  FeedbackDto,
  FileDownloadUrlDtoResponse,
  IdName,
  MemSourceProjectLinkDto,
  MemSourceProjectLinksDtoResponse,
  MemsourceTaskJobsCompletion,
  PreprocessingDeliveryInfoDto,
  PreprocessingInputDataDto,
  QAFormDto,
  TaskBaseFileDto,
  TaskBaseFileDtoResponse,
  TaskDtoResponse,
  TaskInstructionListDto,
  Translate5ProjectLinksDtoResponse,
  USER_TYPES,
  UserTypeCode,
  XtmProjectDto,
  XtmProjectsDtoResponse,
  ZipFullDownloadDtoResponse,
  PortalUploadModeEnum,
} from "api/api.typing";
import { requestDownloadFile } from "api/utils";
import { AxiosError, AxiosResponse } from "axios";
import { DateTime, Duration } from "luxon";
import { TaskFileUploadType } from "pages/project/common/taskBase.typing";
import { toHuman } from "utils/format";
import { LanguageType, SUPPORTED_LANGUAGES } from "utils/language";

export const fetchTasks = async (userType: UserTypeCode, language: LanguageType): Promise<TaskDtoResponse> => {
  const endpoint = userType === USER_TYPES.customer ? "/api/tasks/customerTasks" : "/api/tasks";
  const response = await api.get<TaskDtoResponse>(
    `${endpoint}?translationLanguageCode=${SUPPORTED_LANGUAGES[language].tempoCode}`
  );

  return response.data;
};

export const fetchTaskById = async (taskId: string, language: LanguageType) => {
  const response = await api.get(
    `/api/tasks/${taskId}?translationLanguageCode=${SUPPORTED_LANGUAGES[language].tempoCode}`
  );
  return response.data;
};

export const fetchPreprocessingDeliveryInfo = async (taskId: string): Promise<PreprocessingDeliveryInfoDto> => {
  const response = await api.get<PreprocessingDeliveryInfoDto>(`/api/tasks/${taskId}/preprocessingDeliveryInfo`);
  return response.data;
};

export const fetchPreprocessingInputData = async (taskId: string): Promise<PreprocessingInputDataDto> => {
  const response = await api.get<PreprocessingInputDataDto>(`/api/tasks/${taskId}/preprocessing-input-data`);
  return response.data;
};

export const fetchDeliveryFileMappingOptions = async (
  taskId: string,
  deliveryFileNames: string[]
): Promise<DeliveryFileMappingOptionsDto> => {
  const response = await api.post<DeliveryFileMappingOptionsDto>(`/api/tasks/${taskId}/delivery-file-mapping-options`, {
    deliveryFileNames,
  });
  return response.data;
};

export const requestDeliverTask = async (
  taskId: string,
  fullDelivery: boolean,
  comment?: string,
  remarks?: string,
  qaFormRatings?: Array<{ key: number; value: number | null }>,
  catToolId?: number,
  noteDelivery?: boolean
): Promise<AxiosResponse<void>> => {
  const fileTypeQueryParam = noteDelivery ? `&noteDelivery=${noteDelivery}` : "";
  return await api.put<void>(
    `/api/tasks/${taskId}/deliver?fullDelivery=${fullDelivery}&catToolId=${catToolId}${fileTypeQueryParam}`,
    {
      comment,
      remarks,
      qaFormRatings,
    }
  );
};

export const submitFeedback = async (
  taskId: string,
  comment?: string,
  qaFormRatings?: Array<{ key: number; value: number | null }>
): Promise<AxiosResponse<void>> => {
  return await api.post<void>(`/api/tasks/${taskId}/feedback`, {
    comment,
    qaFormRatings,
  });
};

export const fetchDeliveredFiles = async (taskId: string): Promise<TaskBaseFileDto[]> => {
  const response = await api.get<TaskBaseFileDtoResponse>(`/api/tasks/${taskId}/deliveredFiles`);
  const files = response?.data?.deliveredFiles ?? [];
  return files.sort((a, b) => {
    const timeA = new Date(a.creationTime).getTime();
    const timeB = new Date(b.creationTime).getTime();
    return timeB - timeA;
  });
};

export const fetchMemSourceProjectLinks = async (
  taskId: string,
  signal?: AbortSignal
): Promise<MemSourceProjectLinkDto[]> => {
  const response = await api.get<MemSourceProjectLinksDtoResponse>(`/api/tasks/${taskId}/memsourceProjectLinks`, {
    signal,
  });
  return response?.data?.memsourceProjectLinks ?? [];
};

export const fetchTranslate5ProjectLinks = async (
  taskId: string,
  signal?: AbortSignal
): Promise<CatToolProjectLinkDto[]> => {
  const response = await api.get<Translate5ProjectLinksDtoResponse>(`/api/tasks/${taskId}/translate5ProjectLinks`, {
    signal,
  });
  return response?.data?.catToolProjectLinks ?? [];
};

export const fetchXtmProject = async (taskId: string, signal?: AbortSignal): Promise<XtmProjectDto[]> => {
  const response = await api.get<XtmProjectsDtoResponse>(`/api/tasks/${taskId}/xtmProjects`, { signal });
  return response?.data?.xtmProjects ?? [];
};

export const requestZipFullTaskDownload = async (
  taskId: string,
  language: LanguageType,
  hubConnectionId: string
): Promise<ZipFullDownloadDtoResponse> => {
  const response = await api.post(`/api/tasks/${taskId}/zipFullDownload`, {
    taskId,
    languageCode: SUPPORTED_LANGUAGES[language].tempoCode,
    hubConnectionId,
  });

  return response.data;
};

export const requestDownloadFullTaskZip = async (taskId: string, guid: string): Promise<void> => {
  const response = await api.get<FileDownloadUrlDtoResponse>(`/api/tasks/${taskId}/fullDownloadUrl?guid=${guid}`);
  await requestDownloadFile(response.data.downloadUrl, { name: "file", extension: "zip" });
};

export const requestDownloadPurchaseOrder = async (taskId: string) => {
  const response = await api.get<FileDownloadUrlDtoResponse>(`/api/tasks/${taskId}/purchaseOrderDownloadUrl`);
  await requestDownloadFile(response.data.downloadUrl, { name: "file", extension: "pdf" });
};

export const requestDownloadSupplierPreInvoice = async (preInvoiceId: number) => {
  const response = await api.get<FileDownloadUrlDtoResponse>(`/api/SupplierPreInvoices/${preInvoiceId}/exportUrl`);
  await requestDownloadFile(response.data.downloadUrl, { name: "file", extension: "xlsx" });
};

export async function uploadToS3SingleTaskFile(
  {
    file,
    presignedUrl,
  }: {
    file: File;
    presignedUrl: string;
  },
  onUploading: (abort: AbortController) => void,
  onProgress: (progress: number, remainingTime?: string) => void,
  onUploaded: () => void,
  onFailed: (isCancellation: boolean) => void
): Promise<void> {
  try {
    const forwardUrl = import.meta.env.PROD
      ? presignedUrl
      : presignedUrl.replace("https://tempo-dev-01.s3.eu-central-1.amazonaws.com", "/forward");
    const abort = new AbortController();
    onUploading(abort);
    const startTime = DateTime.now();
    await api.put(forwardUrl, file.slice(), {
      onUploadProgress: (ev) => {
        const total = ev.total ?? 1;
        const uploadSpeed = ev.loaded / DateTime.now().diff(startTime, "seconds").toMillis();
        const remainingTime = toHuman(Duration.fromMillis((total - ev.loaded) / uploadSpeed));
        onProgress(Math.round((ev.loaded * 100) / total), remainingTime);
      },
      timeout: 0,
      signal: abort.signal,
      headers: {
        "Content-Type": "application/octet-stream",
      },
    });
    onUploaded();
  } catch (error) {
    const isCancellation = (error as AxiosError)?.code === "ERR_CANCELED";
    onFailed(isCancellation);
  }
}

export interface FileMappingDto {
  sourceFileIds?: number[];
  languageCodes: string[];
}

export const getPreSignedUrl = async (
  taskId: string,
  fileNames: string[],
  fileType: TaskFileUploadType,
  portalUploadMode: PortalUploadModeEnum,
  fileMappings?: Record<string, FileMappingDto>
): Promise<Array<{ fileName: string; uploadUrl: string }> | null> => {
  const response = await api.post(`/api/tasks/${taskId}/buildPreSignedUrls`, {
    fileNames,
    fileType,
    portalUploadMode,
    fileMappings,
  });

  return response.data?.value?.results ?? null;
};

export const uploadSingleTaskFile = async (
  taskId: string,
  file: File,
  fileType: TaskFileUploadType,
  onUploading: (abort: AbortController) => void,
  onProgress: (progress: number, remainingTime?: string) => void,
  onUploaded: () => void,
  onFailed: (isCancellation: boolean) => void,
  fileMapping?: FileMappingDto
): Promise<void> => {
  try {
    const abort = new AbortController();
    onUploading(abort);

    // Add file mapping as query parameter if provided
    let url = `/api/tasks/${taskId}/uploadFile?fileType=${fileType}&fileName=${encodeURIComponent(file.name)}`;
    if (fileMapping) {
      url += `&fileMapping=${encodeURIComponent(JSON.stringify(fileMapping))}`;
    }

    const formData = new FormData();
    formData.append(file.name, file);
    const startTime = DateTime.now();
    await api.post(url, formData, {
      onUploadProgress: (ev) => {
        const total = ev.total ?? 1;
        const uploadSpeed = ev.loaded / DateTime.now().diff(startTime, "seconds").toMillis();
        const remainingTime = toHuman(Duration.fromMillis((total - ev.loaded) / uploadSpeed));
        onProgress(Math.round((ev.loaded * 100) / total), remainingTime);
      },
      timeout: 0,
      signal: abort.signal,
    });
    onUploaded();
  } catch (error) {
    const isCancellation = (error as AxiosError)?.code === "ERR_CANCELED";
    onFailed(isCancellation);
  }
};

export const fetchQAForm = async (taskId: string): Promise<QAFormDto> => {
  const response = await api.get<QAFormDto>(`/api/tasks/${taskId}/qaform`);
  return response.data;
};

export const fetchSupplierContacts = async (): Promise<IdName[]> => {
  const url = `/api/tasks/supplierContacts`;
  const response = await api.get<IdName[]>(url);
  return response?.data;
};

export const updateSupplierContact = async (taskId: string, supplierContactId: number): Promise<void> => {
  const url = `/api/tasks/${taskId}/supplierContacts`;
  await api.put(url, { supplierContactId: supplierContactId });
};

export const fetchXtmStepsProgress = async (taskId: string, signal?: AbortSignal): Promise<CatStepsDto> => {
  const result = await api.get<CatStepsDto>(`/api/tasks/${taskId}/xtmStepsProgress`, { signal });
  return result.data;
};

export const fetchTaskInstructions = async (
  taskId: string,
  language: LanguageType,
  signal?: AbortSignal
): Promise<TaskInstructionListDto> => {
  const result = await api.get<TaskInstructionListDto>(
    `/api/tasks/${taskId}/instructions?languageCode=${SUPPORTED_LANGUAGES[language].tempoCode}`,
    { signal }
  );
  return result.data;
};

export const acknowledgeTaskInstructions = async (taskId: string): Promise<void> => {
  await api.post(`/api/tasks/${taskId}/instructions/acknowledgement`);
};

export const fetchMemSourceStepsProgress = async (taskId: string, signal?: AbortSignal): Promise<CatStepsDto> => {
  const response: AxiosResponse<CatStepsDto> = await api.get(`/api/tasks/${taskId}/memsourceStepsProgress`, {
    signal,
  });
  return response.data;
};

export const fetchMemsourceJobsCompletion = async (
  taskId: string,
  signal?: AbortSignal
): Promise<MemsourceTaskJobsCompletion> => {
  const response: AxiosResponse<MemsourceTaskJobsCompletion> = await api.get(
    `/api/tasks/${taskId}/memsourceTaskJobsCompletion`,
    {
      signal,
    }
  );
  return response.data;
};

export const requestGenerateXtmQAReport = async (
  taskId: string,
  hubConnectionId: string
): Promise<{ reportUrl?: string; status: string; value?: string | null }> => {
  const response = await api.post(`/api/tasks/${taskId}/generateXtmQaReport`, {
    taskId,
    hubConnectionId,
  });

  return response.data;
};

export interface XtmQAReport {
  xtmQaReportName: string;
  xtmQaReportUrl: string;
}

export interface XtmQAReportList {
  xtmQaReports: XtmQAReport[];
}

export const fetchXtmQAReport = async (taskId: string): Promise<XtmQAReportList> => {
  const response: AxiosResponse<XtmQAReportList> = await api.get<XtmQAReportList>(`/api/tasks/${taskId}/xtmQaReport`);
  return response.data;
};

export const fetchFeedbacks = async (language: LanguageType): Promise<FeedbackDto[]> => {
  const response = await api.get<FeedbackDto[]>(
    `/api/tasks/feedbacks?translationLanguageCode=${SUPPORTED_LANGUAGES[language].tempoCode}`
  );
  return response.data;
};

export const skipFeedback = async (taskId: number): Promise<void> => {
  await api.post(`/api/tasks/${taskId}/skipFeedback`);
};
